import { z } from 'zod';
import { SchemaFormDefinition, SchemaFormFields } from '@mews-commander/form';
import { Option } from '@mews-commander/types';
import { lt } from '@mews/framework';
import { Container, Stack } from '@optimus-web/core';
import { ContractsTextKey as TextKey } from '../../ContractsTextKey';
import { useExistingTemplateNames } from '../externalTemplates/templatesTable/hooks/useExistingTemplateNames';
import { TemplateFormStepFields } from './dataMapping/constants';

export const templateOptionsSchema = (existingNames: string[]) =>
    z.object({
        name: z
            .string()
            .min(1)
            .max(255)
            .refine((name) => !existingNames.includes(name), {
                message: TextKey.ContractTemplateFormDuplicateNameValidationMessage,
            }),
        description: z.string().max(1000).optional().nullable(),
        services: z.array(z.string()).min(1),
        template: z.string().min(1),
    });

export type TemplateOptionsValues = z.infer<ReturnType<typeof templateOptionsSchema>>;

const templateOptionsFormDefinition = ({
    serviceOptions,
    templateOptions,
}: {
    serviceOptions?: Option[];
    templateOptions?: Option[];
}): SchemaFormDefinition<ReturnType<typeof templateOptionsSchema>> => ({
    fields: {
        name: {
            label: lt('ContractTemplateNameLabel'),
        },
        description: {
            label: lt('Description'),
            field: {
                type: 'textarea',
            },
        },
        services: {
            label: lt('ContractTemplateServiceFieldLabel'),
            field: {
                type: 'select',
                options: serviceOptions ?? [],
                showEmptyValue: false,
            },
        },
        template: {
            label: lt('ContractTemplateDocusignTemplateFieldLabel'),
            field: {
                type: 'select',
                options: templateOptions ?? [],
                showEmptyValue: false,
            },
        },
    },
});

export const TemplateOptionsForm = ({
    serviceOptions,
    templateOptions,
}: {
    serviceOptions?: Option[];
    templateOptions?: Option[];
}) => {
    const existingTemplateNames = useExistingTemplateNames();
    return (
        <Stack vertical itemAlignment="center">
            <Container maxWidth="640px" width="100%">
                <SchemaFormFields
                    schema={templateOptionsSchema(existingTemplateNames)}
                    formDefinition={templateOptionsFormDefinition({ serviceOptions, templateOptions })}
                    namePrefix={TemplateFormStepFields.templateOptionsFields}
                />
            </Container>
        </Stack>
    );
};
